import { z } from 'zod';

import { authFetch } from '@verantos-dev/core-utils';

// Only the URL is read — S3's `Content-Disposition` names the file.
const conceptGroupExportSchema = z.object({ url: z.url() });

// Long enough for S3 to answer and the browser to adopt the download.
const DOWNLOAD_FRAME_TEARDOWN_MS = 60_000;

/**
 * Hand the browser off to S3 for a real download. An iframe because `_blank` is
 * popup-blocked after the await, and same-tab would navigate away on S3's XML.
 */
const followDownloadUrl = (url: string): void => {
  const frame = document.createElement('iframe');
  frame.style.display = 'none';
  frame.src = url;
  document.body.appendChild(frame);
  window.setTimeout(() => frame.remove(), DOWNLOAD_FRAME_TEARDOWN_MS);
};

interface DownloadConceptGroupsWorkbookInput {
  /** Not sent — the request is scoped by the caller's organization. Keys the
   *  in-flight map, so two projects can export at once. */
  projectId: string;
  /** Re-scoped server-side, so a stale list yields a smaller file rather than
   *  reaching anything it shouldn't. */
  conceptGroupIds: string[];
}

// One in-flight request per project, so a re-mounted modal joins the running
// promise. Nothing aborts it — closing the modal never cancels the download.
const inFlightByProject = new Map<string, Promise<void>>();

// Observable so UI outside the modal can react; the map is module state.
const inFlightListeners = new Set<() => void>();
const notifyInFlightChange = (): void => inFlightListeners.forEach((listener) => listener());

/** Subscribe to in-flight export changes; returns the unsubscribe function. */
export const subscribeToConceptGroupExportsInFlight = (listener: () => void): (() => void) => {
  inFlightListeners.add(listener);
  return () => inFlightListeners.delete(listener);
};

/** Whether an export for this project is currently in flight. */
export const isConceptGroupExportInFlight = (projectId: string): boolean =>
  inFlightByProject.has(projectId);

/**
 * Build the selected groups into one xlsx. Not a react-query mutation: its observer
 * detaches during StrictMode's re-subscribe, so success would never be reported.
 */
export const downloadConceptGroupsWorkbook = ({
  projectId,
  conceptGroupIds,
}: DownloadConceptGroupsWorkbookInput): Promise<void> => {
  const existing = inFlightByProject.get(projectId);
  if (existing) return existing;

  const request = (async () => {
    const response = await authFetch('/concept-groups/bulk-export', {
      method: 'POST',
      body: JSON.stringify({ conceptGroupIds }),
    });
    followDownloadUrl(conceptGroupExportSchema.parse(response).url);
  })().finally(() => {
    inFlightByProject.delete(projectId);
    notifyInFlightChange();
  });

  inFlightByProject.set(projectId, request);
  notifyInFlightChange();
  return request;
};
